import BaseParser, {
  FhirResourceParserSupportedTypes,
  SortValue,
} from './BaseParser';
import _get from 'lodash/get';
import {
  BaseParserOptions,
  FhirArrayOptions,
  FhirArrayOptionalPathParser,
} from './SharedTypes';

class FhirArray extends BaseParser {
  private optionalPathParsers: FhirArrayOptionalPathParser[];
  private arrayElementParser: BaseParser;
  private arrayElementPath: string;
  private concatenate: boolean;

  /**
   * @param {String} options.fhirArray.arrayElementPath - The path to the target property to parse within an enumerated array element
   * @param {BaseParser} options.fhirArray.arrayElementParser - The parser to use on the target property within the array
   * @param {Object[]} options.fhirArray.optionalPathParser - Paths and corresponding type parsers for optional fhir properties
   * @param {Boolean} [options.fhirArray.concatenate = false] - Flag determining if the parsed array values are concatenated or if only the first element is retured
   */
  constructor(options = {}) {
    super();

    const {
      arrayElementParser,
      arrayElementPath,
      optionalPathParsers,
      concatenate,
    } = _get<BaseParserOptions, 'fhirArray', FhirArrayOptions>(
      options,
      'fhirArray',
      {},
    );

    if (
      !arrayElementParser &&
      _get<FhirArrayOptionalPathParser[], 'length', number>(
        optionalPathParsers,
        'length',
        0,
      ) === 0
    ) {
      throw new Error(
        'Array parser must be supplied a parser for the child element or atleast one optional path parser',
      );
    }

    this.optionalPathParsers = optionalPathParsers || [];
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this.arrayElementParser = arrayElementParser;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this.arrayElementPath = arrayElementPath; // default to just parsing the enumerated object
    this.concatenate = !!concatenate;
  }

  getArrayElementParserAndValue = (
    arrayElement: FhirResourceParserSupportedTypes | any,
  ) => {
    if (this.optionalPathParsers.length !== 0) {
      const pathParser = this.optionalPathParsers.find(
        ({ arrayElementPath }) =>
          _get(arrayElement, arrayElementPath) !== undefined,
      );

      if (pathParser) {
        return {
          parser: pathParser.arrayElementParser,
          value: _get(arrayElement, pathParser.arrayElementPath),
        };
      }
    }

    return {
      parser: this.arrayElementParser,
      value:
        this.arrayElementPath === undefined
          ? arrayElement
          : _get(arrayElement, this.arrayElementPath),
    };
  };

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  getDisplayValue(fhirProperty: FhirResourceParserSupportedTypes[]) {
    const emptyElement = _get(fhirProperty, 'length', 0) === 0;
    if (!fhirProperty || emptyElement) return null;

    const displayValues = fhirProperty.map(arrayElement => {
      const { parser, value } =
        this.getArrayElementParserAndValue(arrayElement);

      return parser ? parser.getDisplayValue(value) : null;
    });

    return this.concatenate
      ? displayValues.filter(val => val != null).join(', ')
      : _get(displayValues, 0, null);
  }

  /**
   * Gets the sort value from the first element in the array
   */
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  getSortValue(fhirProperty: FhirResourceParserSupportedTypes[]) {
    const emptyElement = _get(fhirProperty, 'length', 0) === 0;
    if (!fhirProperty || emptyElement) return null;

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return fhirProperty.reduce(
      (
        sortVal: SortValue,
        arrayElement: FhirResourceParserSupportedTypes,
      ): SortValue => {
        if (sortVal !== null) return sortVal;

        const { parser, value } =
          this.getArrayElementParserAndValue(arrayElement);

        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return parser ? parser.getSortValue(value) : null;
      },
      null,
    );
  }
}

export default FhirArray;
